import React from "react";

interface ConfirmationModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  backdrop?: "static" | boolean;
  children?: React.ReactNode;
  confirmVariant?: string;
  hideFooter?: boolean;
  hideCancel?: boolean;
  style?: React.CSSProperties;
  disabledConfirm?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  show,
  onHide,
  onConfirm,
  title = "Confirm",
  message = "Are you sure you want to continue?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  backdrop = "static",
  children,
  confirmVariant = "primary",
  hideFooter = false,
  hideCancel = false,
  style = {},
  disabledConfirm = false,
}) => {
  if (!show) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (backdrop !== "static") {
      onHide();
    }
  };

  const getConfirmStyle = () => {
    if (confirmVariant === "danger") return { backgroundColor: "#dc2626", color: "#ffffff" }; // red-600
    if (confirmVariant === "success") return { backgroundColor: "#16a34a", color: "#ffffff" }; // green-600
    return { backgroundColor: "#356C58", color: "#ffffff" }; // inception green
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 transform transition-all"
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button 
            onClick={onHide}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <span className="text-2xl leading-none">&times;</span>
          </button>
        </div>

        <div className="px-4 py-4 text-sm text-gray-600">
          {message && <p>{message}</p>}
          {children}
        </div>

        {!hideFooter && (
          <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-lg">
            {!hideCancel && (
              <button
                type="button"
                onClick={onHide}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {cancelText}
              </button>
            )}
            <button
              type="button"
              onClick={onConfirm}
              disabled={disabledConfirm}
              className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
              style={getConfirmStyle()}
            >
              {confirmText}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmationModal;
